import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X as XIcon, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/store/cart";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const attempts = useRef(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) {
            setState("paid");
            clear();
          }
          return;
        }
      } catch (err) {
        console.error("Payment status poll failed:", err);
      }
      if (attempts.current >= 10) {
        if (!cancelled) setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "polling" && (
        <>
          <Loader2 className="mx-auto animate-spin text-[#FF3D8B] mb-6" size={48} />
          <h1 className="font-display text-4xl text-white">Confirming your order…</h1>
          <p className="text-white/50 mt-3 text-sm">Hang tight — this takes a second.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <div className="mx-auto w-20 h-20 rounded-full bg-[#FF3D8B] text-black flex items-center justify-center mb-6 glow-pink">
            <Check size={40} strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-5xl text-white">You're in.</h1>
          <p className="text-white/60 mt-4 max-w-md mx-auto leading-relaxed">
            Order confirmed. We'll email you the details and ship it out — discreetly, always.
          </p>
          <Link
            to="/shop"
            data-testid="success-back-to-shop"
            className="mt-10 inline-flex px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors"
          >
            Keep Shopping
          </Link>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <div className="mx-auto w-20 h-20 rounded-full border border-white/20 text-white/70 flex items-center justify-center mb-6">
            <Loader2 size={40} />
          </div>
          <h1 className="font-display text-4xl text-white">Still processing.</h1>
          <p className="text-white/60 mt-4">
            Payment may still be finalizing. Check your email in a few minutes.
          </p>
          <Link
            to="/shop"
            className="mt-10 inline-flex px-8 py-4 rounded-full border border-white/20 text-white uppercase tracking-wider text-sm hover:border-[#FF3D8B] hover:text-[#FF3D8B] transition-colors"
          >
            Back to Shop
          </Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-cancel-page">
      <div className="mx-auto w-20 h-20 rounded-full border border-white/20 text-white/70 flex items-center justify-center mb-6">
        <XIcon size={40} />
      </div>
      <h1 className="font-display text-4xl text-white">Checkout cancelled.</h1>
      <p className="text-white/60 mt-4">Your cart is still saved. Come back when you're ready.</p>
      <Link
        to="/shop"
        className="mt-10 inline-flex px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors"
      >
        Back to Shop
      </Link>
    </div>
  );
}
